import { createFileRoute } from "@tanstack/react-router";
import { Plug, Trash2 } from "lucide-react";
import { McpClientLogo } from "@/components/integrations/McpClientLogo";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc/client";

export const Route = createFileRoute(
  "/_auth/_dashboard/settings/_layout/connected-apps",
)({
  component: ConnectedAppsPage,
});

const formatDate = (value: string | null) => {
  if (!value) return "Never";
  return new Date(value).toLocaleDateString();
};

const SCOPE_LABELS: Record<string, string> = {
  "clients.read": "View clients",
  "checkins.read": "View check-ins",
};

const scopeLabel = (scope: string) => SCOPE_LABELS[scope] ?? scope;

function ConnectedAppsPage() {
  const grants = trpc.integrations.listMcpGrants.useQuery();
  const utils = trpc.useUtils();
  const revoke = trpc.integrations.revokeMcpGrant.useMutation({
    onSuccess: async () => {
      await utils.integrations.listMcpGrants.invalidate();
      toast.success("Access revoked");
    },
    onError: (error) => {
      toast.error("Failed to revoke access", { description: error.message });
    },
  });
  const [revokingId, setRevokingId] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Connected apps</h3>
        <p className="text-sm text-muted-foreground">
          Apps that can read your Trainrr data through MCP. Revoking removes
          access immediately.
        </p>
      </div>
      <Separator />

      {grants.isLoading ? (
        <div className="space-y-3 rounded-md border p-4">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-64" />
        </div>
      ) : grants.data && grants.data.length > 0 ? (
        <div className="divide-y rounded-md border">
          {grants.data.map((grant) => (
            <div
              key={grant.id}
              className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 gap-3">
                <McpClientLogo
                  name={grant.clientName}
                  className="h-10 w-10 rounded-md text-sm"
                />
                <div className="min-w-0 space-y-1">
                  <p className="truncate text-sm font-medium">
                    {grant.clientName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {grant.orgName} · {grant.scopes.map(scopeLabel).join(", ")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Connected {formatDate(grant.createdAt)} · Last used{" "}
                    {formatDate(grant.lastUsedAt)}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setRevokingId(grant.id);
                  revoke.mutate({ grantId: grant.id });
                }}
                disabled={revokingId === grant.id && revoke.isPending}
              >
                <Trash2 className="h-4 w-4" />
                Revoke
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md border border-dashed p-6">
          <div className="flex max-w-md flex-col gap-2">
            <Plug className="h-8 w-8 text-muted-foreground" />
            <h4 className="text-sm font-medium">No connected apps</h4>
            <p className="text-sm text-muted-foreground">
              When you connect Claude or another MCP client, it will appear
              here so you can revoke access.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
